import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Phone, TrendingUp, AlertTriangle, Users } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import SiteScoreboardCard from './executive/SiteScoreboardCard';
import ComplianceTrendCard from './ComplianceTrendCard';
import { AnalyticsFilters, formatDaysLabel, isCustomRange } from './AnalyticsFilters';
import UpcomingAppointmentsCard from './UpcomingAppointmentsCard';
import { ExecCommsMiniRow } from './ExecCommsMiniRow';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAL = '#09D6B4';

const KpiCard = ({ title, value, icon: Icon, sub, color = 'text-slate-900', iconColor = 'text-slate-400', testId, onClick }) => (
  <Card
    className={`border-slate-200 shadow-sm transition-all ${onClick ? 'hover:shadow-md hover:border-teal-300 cursor-pointer' : ''}`}
    data-testid={testId}
    onClick={onClick}
  >
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-slate-600">{title}</CardTitle>
      <Icon className={`w-5 h-5 ${iconColor}`} />
    </CardHeader>
    <CardContent>
      <div
        data-kpi-value
        className={`text-3xl font-bold ${color} ${onClick ? 'underline decoration-dotted decoration-slate-300' : ''}`}
      >{value}</div>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      {onClick && <p className="text-xs text-teal-500 mt-1">Click to view records</p>}
    </CardContent>
  </Card>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg">
      <p className="text-sm font-medium text-slate-700 mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey || p.name} className="text-sm" style={{ color: p.color }}>
          {p.name}: <span className="font-semibold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

const ExecutiveTab = ({ domainId = null, onSwitchTab = null }) => {
  const { userIndustry, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subtypeRows, setSubtypeRows] = useState(null);
  const [filterDays, setFilterDays] = useState(7);   // Spec Sep-2026: default last 7 days
  const [filterSite, setFilterSite] = useState(null);   // null = all sites in scope

  // Domain the sub-intent panel should query. Master admin viewing a
  // specific tenant → use the prop. Every other role (domain_admin /
  // domain_supervisor) → their own domain_id from the auth context.
  const effectiveDomainId = domainId || user?.domain_id || null;

  useEffect(() => {
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    else if (userIndustry) p.set('call_type', userIndustry);
    if (filterDays) p.set('days', String(filterDays));
    if (filterSite) p.set('site', filterSite);
    const qs = p.toString() ? `?${p}` : '';
    axios.get(`${API}/analytics/executive${qs}`, { timeout: 35000 })
      .then(r => setData(r.data))
      .catch(() => {
        // Fall through to empty state — no toast, no panic.
        // (Network/server hiccups are common on cold Atlas connections.)
        setData({ kpis: { total_calls: 0 } });
      })
      .finally(() => setLoading(false));
  }, [userIndustry, domainId, filterDays, filterSite]);

  // Fetch the sub-intent breakdown for this domain. Runs alongside the
  // main executive load — a supervisor now sees the same "which
  // conversation types are dragging us down" table admins get.
  useEffect(() => {
    if (!effectiveDomainId) { setSubtypeRows([]); return; }
    axios.get(`${API}/analytics/subtype-breakdown?domain_id=${effectiveDomainId}&days=30`, { timeout: 35000 })
      .then(r => setSubtypeRows(r.data?.rows || []))
      .catch(() => setSubtypeRows([]));
  }, [effectiveDomainId]);

  if (loading) return <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>;
  const filtersEl = (
    <AnalyticsFilters
      domainId={domainId}
      days={filterDays}
      site={filterSite}
      onDaysChange={setFilterDays}
      onSiteChange={setFilterSite}
    />
  );
  if (!data?.kpis?.total_calls) return (
    <div>
      {filtersEl}
      <div className="py-16 text-center text-slate-500" data-testid="executive-empty-state">
        <p className="text-base font-medium text-slate-600">No graded calls {filterDays || filterSite ? 'in this filter window' : 'yet'}</p>
        <p className="text-sm text-slate-400 mt-1">
          {filterDays || filterSite
            ? 'Try widening the date range or switching site.'
            : 'Once calls are synced from Yabbit and graded, your executive analytics will appear here.'}
        </p>
      </div>
    </div>
  );

  const { kpis, score_distribution, weekly_trend, type_breakdown } = data;

  // Navigation: for dashboard use react-router, for admin domain pass onSwitchTab
  const goToCalls = (statusFilter = null) => {
    if (onSwitchTab) {
      onSwitchTab('calls', statusFilter);
    } else {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      navigate(`/calls${params}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="executive-tab">
      {filtersEl}
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard title="Total Calls" value={kpis.total_calls} icon={Phone} sub={filterDays ? formatDaysLabel(filterDays) : 'All time'} testId="exec-total-calls" onClick={() => goToCalls()} />
        <KpiCard title="Avg Score" value={`${kpis.avg_score}`} icon={TrendingUp} sub="Out of 100" iconColor="text-teal-500" testId="exec-avg-score" />
        <KpiCard title="Escalated" value={kpis.escalated_calls} icon={AlertTriangle} sub="Below threshold" color="text-rose-600" iconColor="text-rose-400" testId="exec-escalated" onClick={() => goToCalls('escalated')} />
        <KpiCard title="Active Agents" value={kpis.active_agents} icon={Users} sub="Tracked" testId="exec-agents" onClick={() => onSwitchTab ? onSwitchTab('coaching') : null} />
      </div>

      {/* Appointments look FORWARD n days — a custom past range doesn't apply */}
      <UpcomingAppointmentsCard domainId={domainId} site={filterSite} days={isCustomRange(filterDays) ? undefined : filterDays} />

      {/* Comms at a glance — SMS inbox + live chat */}
      <ExecCommsMiniRow />

      {/* Call Type Performance */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Performance by Call Type</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={type_breakdown} layout="vertical" margin={{ top: 5, right: 30, bottom: 5, left: 80 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
              <YAxis dataKey="call_type" type="category" tick={{ fontSize: 11 }} width={80} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="avg_score" name="Avg Score" fill={TEAL} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Site scoreboard — performance by site */}
      <SiteScoreboardCard domainId={effectiveDomainId} days={30} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly Score Trend */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Score Trend (12 Weeks)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={weekly_trend} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
                <defs>
                  <linearGradient id="tealGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={TEAL} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={TEAL} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="avg_score" name="Avg Score" stroke={TEAL} fill="url(#tealGrad)" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Score Distribution */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Score Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={score_distribution} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="range" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Calls" fill={TEAL} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Compliance trend — week-over-week rule-violation rate. Answers
          "is coaching actually reducing rule trips?" */}
      <ComplianceTrendCard domainId={effectiveDomainId} weeks={12} />

      {/* Sub-Intent breakdown — deeper cut into WHICH conversation
          sub-types are dragging the team's average down. Same view the
          admin gets in the Domain → Sub-Intents tab; surfaced here so
          supervisors can act on it without needing admin access. */}
      {subtypeRows && subtypeRows.length > 0 && (
        <Card className="border-slate-200 shadow-sm" data-testid="exec-subtype-breakdown">
          <CardHeader>
            <CardTitle className="text-base">
              Call Sub-Intent Breakdown
              <span className="text-xs font-normal text-slate-400 ml-2">last 30 days</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-200">
                    <th className="py-2 px-2">Call Type</th>
                    <th className="py-2 px-2">Sub-Intent</th>
                    <th className="py-2 px-2 text-right">Calls</th>
                    <th className="py-2 px-2 text-right">Avg Score</th>
                    <th className="py-2 px-2 text-right">Escalated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {subtypeRows.slice(0, 20).map((r, i) => {
                    const escRate = r.count ? Math.round((r.escalated / r.count) * 100) : 0;
                    return (
                      <tr key={`${r.call_type}-${r.subtype}-${i}`} data-testid={`subtype-row-${i}`}>
                        <td className="py-1.5 px-2 text-slate-700 whitespace-nowrap">
                          {(r.call_type || '—').replace(/_/g, ' ')}
                        </td>
                        <td className="py-1.5 px-2 text-slate-600">
                          {r.subtype_label || r.subtype || '—'}
                        </td>
                        <td className="py-1.5 px-2 text-right tabular-nums text-slate-700">
                          {r.count}
                        </td>
                        <td className={`py-1.5 px-2 text-right tabular-nums font-semibold ${
                          r.avg_score >= 70 ? 'text-emerald-600'
                          : r.avg_score >= 50 ? 'text-amber-600'
                          : 'text-red-600'
                        }`}>
                          {r.avg_score != null ? Math.round(r.avg_score) : '—'}
                        </td>
                        <td className="py-1.5 px-2 text-right">
                          <span className={`inline-block px-1.5 py-0.5 rounded font-semibold ${
                            escRate >= 70 ? 'bg-red-100 text-red-700'
                            : escRate >= 40 ? 'bg-amber-100 text-amber-700'
                            : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {escRate}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExecutiveTab;
